import { all, call, cancelled, fork, put, race, select } from 'redux-saga/effects';

import { GeneratorResult, OperationStatus, selectTyped, takeIf } from '@pressreader/appstore';
import { loadIssueRestrictionsByIssueKey } from '@pressreader/content';
import { getArticle, getArticles, type IArticleDto, mapArticle } from '@pressreader/content-api';
import { ArticleAccess, ArticleFields, IArticle } from '@pressreader/content-types';
import { getKnownMissingArticleFields } from '@pressreader/content-utils';
import { ArticleLoadingTags } from '@pressreader/feed-types';
import { error } from '@pressreader/logger';
import { ExtractReturnType } from '@pressreader/types';
import { selectScreenInfo } from '@pressreader/ui-common';
import { newGuid } from '@pressreader/utils';

import { selectImagesConfig } from '../../global.selectors';
import { mergeArticlesByAvailableFields } from '../../utils/article/merge.articles.by.available.fields';
import { articleUpdated, loadArticleBegan, loadArticleCancelled, loadArticleDone, loadArticleFailed } from '../actions';
import { selectArticleByIdAndLanguage, selectArticleMetaById, selectorContentIsHyphenated } from '../selectors';

import { loadArticleBookmarks } from './share';

interface ArticleLoadOptions {
    /** Fields that should be acquired. */
    requiredFields: ArticleFields;

    /** Forcefully load article data. */
    force?: boolean;

    /**
     * Translation language.
     *
     * `string` requests that language, `null` requests the original language explicitly, and
     * `undefined` leaves the decision to the cached version's own language.
     */
    lang?: string | null;

    /** If access requires confirmation, do it. */
    confirm?: boolean;

    /** Define if Hyphenated is enabled, default true */
    isHyphenated?: boolean;

    /**
     * When true, known restricted fields are ignored during missing-fields pruning.
     * Used for access-transition reloads when article access may have changed.
     */
    resetRestrictions?: boolean;
}

function* createArticleFromDTO(articleDto: IArticleDto, requiredFields: ArticleFields): GeneratorResult<IArticle> {
    if (!articleDto) {
        throw new Error('Parse article failed.');
    }

    const screenInfo: ReturnType<typeof selectScreenInfo> = yield select(selectScreenInfo);
    const { imageServers, imagesSupported }: ReturnType<typeof selectImagesConfig> = yield select(selectImagesConfig);
    const article = mapArticle({ screenInfo, imageServers, imagesSupported }, articleDto, requiredFields);

    return article;
}

/**
 * The version of a settled entry's article cached for `lang`: `undefined` keeps the entry's own version.
 */
function* pickCachedArticleVersion(
    article: IArticle | null,
    articleId: string,
    lang?: IArticle['language'] | null,
): GeneratorResult<IArticle | null> {
    // `null` is a requested language too — it picks the original-language version.
    if (lang !== undefined && article?.language !== lang) {
        return yield* selectTyped(selectArticleByIdAndLanguage, { articleId, language: lang });
    }

    return article;
}

function* getOrWaitForArticle(articleId: string, lang?: IArticle['language'] | null): GeneratorResult<IArticle | null> {
    const articleMeta: ReturnType<typeof selectArticleMetaById> = yield select(selectArticleMetaById, { articleId });

    if (!articleMeta) {
        return null;
    }

    if (articleMeta.loading !== OperationStatus.None) {
        if (articleMeta.loading === OperationStatus.Done) {
            return yield* pickCachedArticleVersion(articleMeta.article, articleId, lang);
        }

        if (articleMeta.loading === OperationStatus.Failed) {
            return null;
        }

        yield race([
            takeIf(loadArticleDone, a => a.payload.article.id === articleId),
            takeIf(loadArticleFailed, a => a.payload.articleId === articleId),
            takeIf(loadArticleCancelled, a => a.payload.articleId === articleId),
        ]);

        return yield* getOrWaitForArticle(articleId, lang);
    }

    return null;
}

interface CachedArticleJudgement {
    /** The fields still to request: the caller's, widened for a partial body and pruned of fields known to be missing / never loadable. */
    requiredFields: ArticleFields;

    /** The language the request names (undefined = original/omitted), after the cached version's own language is taken into account. */
    requiredLang: string | null | undefined;

    translationRequired: boolean;

    /** True when the cached version answers the request without a network call (`!force` callers return it as is). */
    satisfied: boolean;
}

/**
 * The language the request names (`undefined` = original/omitted) once the cached version's own language is
 * taken into account, and whether answering the request needs a translation.
 */
function judgeRequiredLang(
    article: IArticle | null,
    lang: ArticleLoadOptions['lang'],
): Pick<CachedArticleJudgement, 'requiredLang' | 'translationRequired'> {
    if (!article) {
        return { requiredLang: lang, translationRequired: false };
    }

    // no language was requested, so keep the cached version's own language
    const requestedLang = lang === undefined ? article.language : lang;
    const requiredLang = article.originalLanguage === requestedLang ? undefined : requestedLang;
    const isTranslated = article.language !== article.originalLanguage;

    return { requiredLang, translationRequired: requiredLang ? requiredLang !== article.language : isTranslated };
}

function judgeCachedArticle(
    article: IArticle | null,
    options: Pick<ArticleLoadOptions, 'requiredFields' | 'lang' | 'force' | 'resetRestrictions'>,
): CachedArticleJudgement {
    const { requiredLang, translationRequired } = judgeRequiredLang(article, options.lang);
    let requiredFields = options.requiredFields;
    let satisfied = false;

    if (article?.availableTextLength !== article?.textLength) {
        requiredFields |= ArticleFields.Paragraphs;
    }

    if (article) {
        const knownMissingFields = getKnownMissingArticleFields(article, { resetRestricted: options.resetRestrictions });

        requiredFields &= ~knownMissingFields;
    }

    if (article && !options.force) {
        if (article.access && article.access !== ArticleAccess.Granted) {
            // required fields could be not loaded because user doesn't have access to them,
            // they will never be loaded, so just assume that they are not required
            // it will avoid unecessary call to server
            requiredFields &= ~ArticleFields.Paragraphs;
        }

        satisfied = ((article.availableFields | article.restrictedFields) & requiredFields) === requiredFields && !translationRequired;
    }

    return { requiredFields, requiredLang, translationRequired, satisfied };
}

/**
 * A non-waiting read of a settled article: the cached version of a `Done` entry for `lang`, or `null`.
 */
function* findCachedArticle(articleId: string, lang?: IArticle['language'] | null): GeneratorResult<IArticle | null> {
    const articleMeta = yield* selectTyped(selectArticleMetaById, { articleId });

    if (!articleMeta || articleMeta.loading !== OperationStatus.Done) {
        return null;
    }

    return yield* pickCachedArticleVersion(articleMeta.article, articleId, lang);
}

/**
 * Load article by ID by either returning cached article or fetching it from server.
 *
 * @returns the cached or newly loaded article; after a failed request, a cached version that satisfies the request
 * (an overlapping load settled it); otherwise null.
 */
function* loadArticleById(articleId: string, options: ArticleLoadOptions = { requiredFields: ArticleFields.None }): GeneratorResult<IArticle | null> {
    let { isHyphenated } = options;
    const { confirm } = options;
    const article = yield* getOrWaitForArticle(articleId, options.lang);

    const judgement = judgeCachedArticle(article, options);
    if (judgement.satisfied) {
        return article;
    }

    const { requiredLang, translationRequired } = judgement;
    let requiredFields = judgement.requiredFields;

    const loadingTags: ArticleLoadingTags[] = [];
    if (translationRequired) {
        loadingTags.push(ArticleLoadingTags.Translation);
    }

    const contentIsHyphenated: ReturnType<typeof selectorContentIsHyphenated> = yield select(selectorContentIsHyphenated);
    isHyphenated = isHyphenated === undefined ? contentIsHyphenated : isHyphenated;

    const loadId = newGuid();

    try {
        // Inside the `try`, so the `finally` answers a cancellation that lands while this put is still
        // queued: a put is never cancelled, and the Pending it marks would otherwise outlive its owner.
        // Only the request is announced here — a cached version is not overridden.
        yield put(loadArticleBegan({ articleId, loadingTags, requiredFields, loadId }));

        if (article) {
            requiredFields |= article.availableFields;
        }

        const data = {
            articleId,
            fields: requiredFields,
            // The request carries a language only for a translation: the original is the omitted parameter.
            lang: requiredLang ?? undefined,
            confirm,
            isHyphenated,
        };

        const articleDto: ExtractReturnType<typeof getArticle> = yield call(getArticle, data);
        if (!articleDto) {
            throw new Error('Load article failed.');
        }

        const mappedArticle = yield* createArticleFromDTO(articleDto, requiredFields);
        const mergedArticle = article ? mergeArticlesByAvailableFields(article, mappedArticle) : mappedArticle;

        yield put(loadArticleDone({ article: mergedArticle, requiredFields }));
        return mergedArticle;
    } catch (e) {
        error(e);
        yield put(loadArticleFailed({ articleId, loadId }));

        const cached = yield* findCachedArticle(articleId, options.lang);
        return judgeCachedArticle(cached, options).satisfied ? cached : null;
    } finally {
        // Cancellation unwinds through `finally`, never `catch`: release the `Pending` this load marked,
        // while it still owns the entry, so later waiters are not blocked for the page's life.
        const isCancelled: boolean = yield cancelled();
        if (isCancelled) {
            yield put(loadArticleCancelled({ articleId, loadId }));
        }
    }

    return null;
}

export function* loadArticlesById(params: Parameters<typeof getArticles>[0]): GeneratorResult<IArticle[] | null> {
    try {
        if (params.articleIds.length === 0) {
            return [];
        }

        const articleDTOs: ExtractReturnType<typeof getArticles> = yield getArticles(params);
        const articles: IArticle[] = yield all(
            articleDTOs.filter(article => !!article).map(articleDto => createArticleFromDTO(articleDto, params.fields)),
        );

        yield all(articles.map(article => put(loadArticleDone({ article }))));
        return articles;
    } catch (e) {
        error(new Error('Load articles failed.'), e);
    }
    return null;
}

function* loadArticleAdditionalData(article: IArticle) {
    yield* loadArticleBookmarks(article.primaryArticleId);

    if (article.issue) {
        yield* loadIssueRestrictionsByIssueKey(article.issue.issueKey);
    }
}

export type LoadFullArticleOptions = Pick<ArticleLoadOptions, 'force' | 'confirm' | 'lang' | 'resetRestrictions'> & {
    /**
     * Skip the bookmarks/issue-restrictions fork. For render-critical prewarms (deep-link
     * entries) that fork would add network calls inside the LCP window; the post-commit
     * full-article load performs the lookup instead, outside the LCP window.
     */
    skipAdditionalData?: boolean;
};

function* loadFullArticle(articleId: string, options?: LoadFullArticleOptions): GeneratorResult<IArticle | null> {
    const requiredFields =
        ArticleFields.ShortContent |
        ArticleFields.Issue |
        ArticleFields.Publication |
        ArticleFields.Images |
        ArticleFields.Related |
        ArticleFields.Paragraphs |
        ArticleFields.Similars |
        ArticleFields.Access |
        ArticleFields.Comments |
        ArticleFields.Votes |
        ArticleFields.AnalyticProfiles |
        ArticleFields.Classification |
        ArticleFields.Summary;
    const article = yield* loadArticleById(articleId, { ...options, requiredFields });

    if (article) {
        if (!options?.skipAdditionalData) {
            yield fork(loadArticleAdditionalData, article);
        }
        yield put(articleUpdated({ articleId, article }));
    }

    return article;
}

export { loadArticleById, loadFullArticle };
